import { useQuery } from '@tanstack/react-query'
import { Building2, Users, Package, TrendingUp } from 'lucide-react'
import { backofficeApi, type BackofficeStats } from '../../lib/api'

function StatCard({
  label,
  value,
  icon: Icon,
  sub,
}: {
  label: string
  value: number | string
  icon: React.ElementType
  sub?: string
}) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-400">{label}</p>
          <p className="text-3xl font-bold text-slate-100 mt-1">{value}</p>
          {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
        </div>
        <div className="w-10 h-10 rounded-lg bg-emerald-600/20 flex items-center justify-center">
          <Icon className="w-5 h-5 text-emerald-400" />
        </div>
      </div>
    </div>
  )
}

const PLAN_LABELS: Record<string, string> = {
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
}

const PLAN_COLORS: Record<string, string> = {
  starter: 'bg-slate-700 text-slate-300',
  professional: 'bg-blue-900/50 text-blue-300',
  enterprise: 'bg-emerald-900/50 text-emerald-300',
}

export function BackofficePage() {
  const { data: stats, isLoading, isError } = useQuery<BackofficeStats>({
    queryKey: ['backoffice', 'stats'],
    queryFn: backofficeApi.getStats,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500" />
      </div>
    )
  }

  if (isError || !stats) {
    return (
      <div className="p-8 text-slate-400">Failed to load stats.</div>
    )
  }

  const planDistribution = stats.plan_distribution ?? {}
  const planEntries = Object.entries(planDistribution).sort(([a], [b]) => a.localeCompare(b))

  return (
    <div className="p-8 space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-100">Back-office</h1>
        <p className="text-sm text-slate-500 mt-1">Platform overview</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          label="Organizations"
          value={stats.total_organizations}
          icon={Building2}
        />
        <StatCard
          label="Users"
          value={stats.total_users}
          icon={Users}
        />
        <StatCard
          label="Active plans"
          value={planEntries.length}
          icon={TrendingUp}
          sub="distinct plan tiers in use"
        />
      </div>

      {/* Plan breakdown */}
      {planEntries.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Package className="w-4 h-4 text-slate-400" />
            <h2 className="text-sm font-semibold text-slate-300">Plan distribution</h2>
          </div>
          <div className="space-y-3">
            {planEntries.map(([plan, count]) => {
              const pct = stats.total_organizations
                ? Math.round((count / stats.total_organizations) * 100)
                : 0
              return (
                <div key={plan}>
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${PLAN_COLORS[plan] ?? 'bg-slate-700 text-slate-300'}`}
                    >
                      {PLAN_LABELS[plan] ?? plan}
                    </span>
                    <span className="text-xs text-slate-400">
                      {count} org{count !== 1 ? 's' : ''} ({pct}%)
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
